import React, { useState } from "react";
import { Box, Flex, Text, Link } from "@chakra-ui/react";
import {
	FiArchive,
	FiFile,
	FiFileText,
	FiImage,
	FiMusic,
	FiVideo,
} from "react-icons/fi";
import { FileableFile } from "types/storage.types";

interface MessageFileCardProps {
	file: FileableFile;
}

export const MessageFileCard: React.FC<MessageFileCardProps> = ({ file }) => {
	// Determine icon based on file type
	const getFileIcon = (filename: string, mimeType?: string) => {
		const ext = filename.split(".").pop()?.toLowerCase();
		const type = mimeType?.toLowerCase();

		if (
			type?.startsWith("image/") ||
			["jpg", "jpeg", "png", "gif", "svg", "webp"].includes(ext || "")
		) {
			return (
				<FiImage
					size={20}
					className="text-blue-500"
				/>
			);
		}
		if (
			type?.startsWith("video/") ||
			["mp4", "avi", "mov", "wmv"].includes(ext || "")
		) {
			return (
				<FiVideo
					size={20}
					className="text-purple-500"
				/>
			);
		}
		if (
			type?.startsWith("audio/") ||
			["mp3", "wav", "ogg", "m4a"].includes(ext || "")
		) {
			return (
				<FiMusic
					size={20}
					className="text-pink-500"
				/>
			);
		}
		if (["zip", "rar", "7z", "tar", "gz"].includes(ext || "")) {
			return (
				<FiArchive
					size={20}
					className="text-yellow-600"
				/>
			);
		}
		if (["pdf", "doc", "docx", "txt"].includes(ext || "")) {
			return (
				<FiFileText
					size={20}
					className="text-red-500"
				/>
			);
		}
		return (
			<FiFile
				size={20}
				className="text-gray-500"
			/>
		);
	};

	const formatFileSize = (bytes?: number) => {
		if (!bytes) return "";
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	return (
		<Link
			href={file.file_path}
			download={file.file_name}
			_hover={{ textDecoration: "none" }}
			isExternal
		>
			<Flex
				align="center"
				gap={2}
				p={2}
				borderRadius="md"
				border="1px solid"
				borderColor="gray.200"
				bg="white"
				_hover={{ bg: "gray.50", borderColor: "gray.300" }}
				transition="all 0.2s"
				cursor="pointer"
			>
				<Box flexShrink={0}>{getFileIcon(file.file_name, file.file_type)}</Box>
				<Box
					flex={1}
					minW={0}
				>
					<Text
						fontSize="sm"
						fontWeight="medium"
						noOfLines={1}
						color="gray.700"
					>
						{file.file_name}
					</Text>
					{file.file_size && (
						<Text
							fontSize="xs"
							color="gray.500"
						>
							{formatFileSize(file.file_size)}
						</Text>
					)}
				</Box>
			</Flex>
		</Link>
	);
};
